import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Build as BuildIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { pressApi } from '../api/production';
import type { IssueWorkDTO } from '../api/press';
import { workerApi, getDesigns } from '../services/api';
import Avatar from '@mui/material/Avatar';
import DesignPreviewDialog from '../components/DesignPreviewDialog';
import designApi from '../api/design';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface PressWork {
  id?: number;
  batch_id?: number | null;
  batch_number?: string | number | null;
  design_no: string;
  garment_part: string;
  size: string;
  quantity_available: number;
  stitched_qty?: number;
  opening_entry_qty?: number;
  quantity_issued?: number;
  quantity_received?: number;
  worker_id?: string;
  worker_name?: string;
  press_rate?: number;
  issue_date?: Date;
  receive_date?: Date;
  defect_quantity?: number;
  design_image?: string | null;
  status: 'PENDING' | 'ASSIGNED' | 'RECEIVED';
  assignment_id?: number;
  receive_id?: number;
}

interface PendingDesignGroup {
  design_no: string;
  design_image?: string | null;
  total_quantity: number;
  garments: Array<{
    garment_part: string;
    total_quantity: number;
    sizes: PressWork[];
  }>;
}

interface IssueDialogEntry {
  work: PressWork;
  include: boolean;
  quantity: number;
  press_rate: number;
  addSampleQty: boolean;
}

interface ReceivedDesignGroup {
  design_no: string;
  design_image?: string | null;
  total_received: number;
  total_defects: number;
  items: PressWork[];
}

interface Worker {
  worker_id: string;
  name: string;
}

interface DesignOption {
  id: number;
  design_no: string;
  image_url?: string | null;
  garment_part?: string | null;
}

interface PartBreakdown {
  issued: number;
  received: number;
}

interface WorkerReportRow {
  worker_id: string;
  worker_name: string;
  total_issued: number;
  total_issued_amount: number;
  total_received: number;
  total_received_amount: number;
  total_defects: number;
  assignments_count: number;
  receives_count: number;
  garment_part_breakdown: Record<string, PartBreakdown>;
}

export default function PressStationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const designFilter = searchParams.get('design');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [pendingWork, setPendingWork] = useState<PressWork[]>([]);
  const [filteredPendingWork, setFilteredPendingWork] = useState<PressWork[]>([]);
  const [assignedWork, setAssignedWork] = useState<PressWork[]>([]);
  const [filteredAssignedWork, setFilteredAssignedWork] = useState<PressWork[]>([]);
  const [receivedWork, setReceivedWork] = useState<PressWork[]>([]);
  const [filteredReceivedWork, setFilteredReceivedWork] = useState<PressWork[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [designOptions, setDesignOptions] = useState<DesignOption[]>([]);
  const [designsLoading, setDesignsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [designSearchFilter, setDesignSearchFilter] = useState(designFilter || '');

  // Issue Dialog
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueWorkerId, setIssueWorkerId] = useState('');
  const [issueDoReceiveEntry, setIssueDoReceiveEntry] = useState(false);
  const [issueEntries, setIssueEntries] = useState<Record<string, IssueDialogEntry>>({});
  const [issueDesignInfo, setIssueDesignInfo] = useState<{ design_no: string; design_image?: string | null } | null>(null);
  const [selectedWork, setSelectedWork] = useState<PressWork | null>(null);

  // Received Date Filter
  const [receivedDateFilter, setReceivedDateFilter] = useState<'today' | 'week' | 'month' | 'quarter' | 'custom'>('month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');

  // Received Details Dialog
  const [receivedDetailsDialogOpen, setReceivedDetailsDialogOpen] = useState(false);
  const [selectedReceivedGroup, setSelectedReceivedGroup] = useState<ReceivedDesignGroup | null>(null);

  // Press rate configuration by garment part (load from localStorage)
  const getPressRates = (): { [key: string]: number } => {
    const saved = localStorage.getItem('pressRates');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved press rates:', e);
      }
    }
    return {
      'TOP': 5,
      'BOTTOM': 2.5,
      'JACKET': 6,
      'DUPATTA': 3,
      'BELT': 2,
      'OTHER': 4
    };
  };
  const PRESS_RATES = getPressRates();

  // Receive Dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({
    quantity_received: 0,
    defect_quantity: 0,
    repair_type_id: null as number | null,
    repair_notes: '',
    notes: ''
  });

  // Manual Job Dialog
  const [manualJobDialogOpen, setManualJobDialogOpen] = useState(false);
  const [manualJobForm, setManualJobForm] = useState({
    design_no: '',
    garment_part: '',
    size: '',
    quantity: 0,
    worker_id: '',
    press_rate: 0
  });

  // Design Preview Dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPreviewDesign, setSelectedPreviewDesign] = useState<any>(null);
  const [selectedPreviewTopDesign, setSelectedPreviewTopDesign] = useState<any>(null);
  const [selectedPreviewBottomDesign, setSelectedPreviewBottomDesign] = useState<any>(null);
  const [loadingCuttingData, setLoadingCuttingData] = useState(false);
  const [selectedDesignNo, setSelectedDesignNo] = useState<string | null>(null);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  // Worker Report
  const [workerReportData, setWorkerReportData] = useState<WorkerReportRow[]>([]);
  const [workerReportGarmentParts, setWorkerReportGarmentParts] = useState<string[]>([]);
  const [workerReportLoading, setWorkerReportLoading] = useState(false);
  const [workerReportDateFilter, setWorkerReportDateFilter] = useState<'today' | 'week' | 'month' | 'quarter' | 'custom'>('month');
  const [workerReportFromDate, setWorkerReportFromDate] = useState<string>('');
  const [workerReportToDate, setWorkerReportToDate] = useState<string>('');

  // Worker Detail Dialog
  const [workerDetailOpen, setWorkerDetailOpen] = useState(false);
  const [workerDetailLoading, setWorkerDetailLoading] = useState(false);
  const [workerDetailData, setWorkerDetailData] = useState<any[]>([]);
  const [workerDetailName, setWorkerDetailName] = useState('');

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWork, setEditingWork] = useState<PressWork | null>(null);
  const [editForm, setEditForm] = useState({
    worker_id: '',
    quantity_issued: 0,
    press_rate: 0,
    issue_date: '',
    receive_date: '',
    quantity_received: 0,
    defect_quantity: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
    fetchWorkers();
  }, [tabValue]);

  useEffect(() => {
    fetchDesignOptions();
  }, []);

  useEffect(() => {
    // Apply design filter to all work lists
    const filterByDesign = (work: PressWork[]) => {
      if (designSearchFilter) {
        return work.filter(w =>
          w.design_no.toLowerCase().includes(designSearchFilter.toLowerCase())
        );
      }
      return work;
    };

    setFilteredPendingWork(filterByDesign(pendingWork));
    setFilteredAssignedWork(filterByDesign(assignedWork));
    setFilteredReceivedWork(filterByDesign(receivedWork));
  }, [pendingWork, assignedWork, receivedWork, designSearchFilter]);

  const pendingDesignGroups = useMemo<PendingDesignGroup[]>(() => {
    const map = new Map<string, {
      design_no: string;
      design_image?: string | null;
      total_quantity: number;
      garments: Record<string, { garment_part: string; total_quantity: number; sizes: PressWork[] }>;
    }>();

    filteredPendingWork.forEach((work) => {
      const existing = map.get(work.design_no) || {
        design_no: work.design_no,
        design_image: work.design_image || null,
        total_quantity: 0,
        garments: {}
      };

      if (!existing.design_image && work.design_image) {
        existing.design_image = work.design_image;
      }

      existing.total_quantity += work.quantity_available;

      const garment = existing.garments[work.garment_part] || {
        garment_part: work.garment_part,
        total_quantity: 0,
        sizes: []
      };

      garment.total_quantity += work.quantity_available;
      garment.sizes.push(work);
      existing.garments[work.garment_part] = garment;

      map.set(work.design_no, existing);
    });

    return Array.from(map.values()).map(group => ({
      design_no: group.design_no,
      design_image: group.design_image || null,
      total_quantity: group.total_quantity,
      garments: Object.values(group.garments)
    }));
  }, [filteredPendingWork]);

  const receivedDesignGroups = useMemo<ReceivedDesignGroup[]>(() => {
    const map = new Map<string, ReceivedDesignGroup>();

    filteredReceivedWork.forEach((work) => {
      const existing = map.get(work.design_no) || {
        design_no: work.design_no,
        design_image: work.design_image || null,
        total_received: 0,
        total_defects: 0,
        items: []
      };

      if (!existing.design_image && work.design_image) {
        existing.design_image = work.design_image;
      }

      existing.total_received += (work.quantity_received || 0);
      existing.total_defects += (work.defect_quantity || 0);
      existing.items.push(work);

      map.set(work.design_no, existing);
    });

    return Array.from(map.values());
  }, [filteredReceivedWork]);

  const fetchData = async () => {
    try {
      setLoading(true);

      let fromDate: Date | undefined;
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (receivedDateFilter) {
        case 'week':
          // Start of current week (Sunday)
          fromDate = new Date(now);
          fromDate.setDate(now.getDate() - now.getDay());
          break;
        case 'month':
          // Start of current month
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          // Start of current quarter
          const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
          fromDate = new Date(now.getFullYear(), quarterMonth, 1);
          break;
        case 'custom':
          // Use custom date range
          if (customDateFrom) {
            fromDate = new Date(customDateFrom);
            fromDate.setHours(0, 0, 0, 0);
          } else {
            fromDate = now;
          }
          break;
        case 'today':
        default:
          fromDate = now;
          break;
      }

      const data = await pressApi.getPendingWork({
        received_from: fromDate.toISOString()
      });

      // Separate by status
      let receivedData = data.filter((w: PressWork) => w.status === 'RECEIVED');
      
      // If custom date range with 'to' date, filter on client side
      if (receivedDateFilter === 'custom' && customDateTo) {
        const toDate = new Date(customDateTo);
        toDate.setHours(23, 59, 59, 999);
        receivedData = receivedData.filter((w: PressWork) => {
          if (!w.receive_date) return false;
          const receiveDate = new Date(w.receive_date);
          return receiveDate <= toDate;
        });
      }

      setPendingWork(data.filter((w: PressWork) => w.status === 'PENDING'));
      setAssignedWork(data.filter((w: PressWork) => w.status === 'ASSIGNED'));
      // Sort received by latest receive_date first
      receivedData.sort((a: PressWork, b: PressWork) => {
        const dateA = a.receive_date ? new Date(a.receive_date).getTime() : 0;
        const dateB = b.receive_date ? new Date(b.receive_date).getTime() : 0;
        return dateB - dateA;
      });
      setReceivedWork(receivedData);
    } catch (err: any) {
      console.error('Error fetching press work:', err);
      setError('Failed to load press work');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when date filter changes
  useEffect(() => {
    if (tabValue === 2) {
      // For custom filter, only refetch when both dates are set or when switching away from custom
      if (receivedDateFilter === 'custom') {
        if (customDateFrom) {
          fetchData();
        }
      } else {
        fetchData();
      }
    }
  }, [receivedDateFilter, customDateFrom, customDateTo]);

  const fetchWorkers = async () => {
    try {
      const data = await workerApi.getAll({ worker_type: 'PRESS' });
      setWorkers(data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  };

  const fetchDesignOptions = async () => {
    try {
      setDesignsLoading(true);
      const response = await api.get('/designs', { params: { pageSize: 200, summary: '1' } });
      const designs = response.data || response;
      const formatted: DesignOption[] = (designs || []).map((design: any) => ({
        id: design.id,
        design_no: design.design_no,
        image_url: design.image_url || design.image2_url || design.image3_url || design.image4_url || null,
        garment_part: design.garment_part || design.category || null
      }));
      setDesignOptions(formatted);
    } catch (err) {
      console.error('Error fetching designs:', err);
    } finally {
      setDesignsLoading(false);
    }
  };

  // Debounced server-side design search for manual job dialog
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDesignSearch = useCallback((query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.length < 2) return; // only search when 2+ chars typed
    searchTimerRef.current = setTimeout(async () => {
      try {
        setDesignsLoading(true);
        const response = await api.get('/designs', { params: { q: query, pageSize: 50, summary: '1' } });
        const designs = response.data || response;
        const searchResults: DesignOption[] = (designs || []).map((design: any) => ({
          id: design.id,
          design_no: design.design_no,
          image_url: design.image_url || design.image2_url || design.image3_url || design.image4_url || null,
          garment_part: design.garment_part || design.category || null
        }));
        // Merge search results with existing options (avoid duplicates)
        setDesignOptions(prev => {
          const existingNos = new Set(prev.map(d => d.design_no));
          const newOnes = searchResults.filter(d => !existingNos.has(d.design_no));
          return [...prev, ...newOnes];
        });
      } catch (err) {
        console.error('Error searching designs:', err);
      } finally {
        setDesignsLoading(false);
      }
    }, 400);
  }, []);

  const fetchWorkerReport = async () => {
    try {
      setWorkerReportLoading(true);
      const now = new Date();
      let fromDate: Date;
      let toDate = new Date();
      toDate.setHours(23, 59, 59, 999);

      switch (workerReportDateFilter) {
        case 'today':
          fromDate = new Date(now);
          fromDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          fromDate = new Date(now);
          fromDate.setDate(now.getDate() - now.getDay());
          fromDate.setHours(0, 0, 0, 0);
          break;
        case 'quarter':
          const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
          fromDate = new Date(now.getFullYear(), quarterMonth, 1);
          break;
        case 'custom':
          fromDate = workerReportFromDate ? new Date(workerReportFromDate) : new Date(now.getFullYear(), now.getMonth(), 1);
          if (workerReportToDate) {
            toDate = new Date(workerReportToDate);
            toDate.setHours(23, 59, 59, 999);
          }
          break;
        case 'month':
        default:
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
      }

      const response = await pressApi.getWorkerReport({
        from_date: fromDate.toISOString(),
        to_date: toDate.toISOString()
      });
      // Backend now returns { data: WorkerReportRow[], garment_parts: string[] }
      if (response.data && response.garment_parts) {
        setWorkerReportData(response.data);
        setWorkerReportGarmentParts(response.garment_parts);
      } else {
        // Fallback for old format
        setWorkerReportData(Array.isArray(response) ? response : []);
        setWorkerReportGarmentParts([]);
      }
    } catch (err: any) {
      console.error('Error fetching worker report:', err);
      setError('Failed to load worker report');
    } finally {
      setWorkerReportLoading(false);
    }
  };

  // Fetch worker detail for the popup
  const fetchWorkerDetail = async (workerId: string, workerName: string) => {
    try {
      setWorkerDetailName(workerName);
      setWorkerDetailOpen(true);
      setWorkerDetailLoading(true);

      const now = new Date();
      let fromDate: Date;
      let toDate = new Date();
      toDate.setHours(23, 59, 59, 999);

      switch (workerReportDateFilter) {
        case 'today':
          fromDate = new Date(now); fromDate.setHours(0, 0, 0, 0); break;
        case 'week':
          fromDate = new Date(now); fromDate.setDate(now.getDate() - now.getDay()); fromDate.setHours(0, 0, 0, 0); break;
        case 'quarter':
          fromDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); break;
        case 'custom':
          fromDate = workerReportFromDate ? new Date(workerReportFromDate) : new Date(now.getFullYear(), now.getMonth(), 1);
          if (workerReportToDate) { toDate = new Date(workerReportToDate); toDate.setHours(23, 59, 59, 999); }
          break;
        case 'month':
        default:
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
      }

      const data = await pressApi.getWorkerReportDetail(workerId, {
        from_date: fromDate.toISOString(),
        to_date: toDate.toISOString()
      });
      setWorkerDetailData(data);
    } catch (err: any) {
      console.error('Error fetching worker detail:', err);
      setError('Failed to load worker detail');
    } finally {
      setWorkerDetailLoading(false);
    }
  };

  // Fetch worker report when tab switches to it or date filter changes
  useEffect(() => {
    if (tabValue === 3) {
      if (workerReportDateFilter === 'custom') {
        if (workerReportFromDate) {
          fetchWorkerReport();
        }
      } else {
        fetchWorkerReport();
      }
    }
  }, [tabValue, workerReportDateFilter, workerReportFromDate, workerReportToDate]);

  const handleIssueOpen = (group: PendingDesignGroup) => {
    const entries: Record<string, IssueDialogEntry> = {};
    const works = group.garments.flatMap(part => part.sizes);

    works.forEach((work, index) => {
      const key = `${work.design_no}-${work.garment_part}-${work.size}-${work.batch_id ?? 'NA'}-${index}`;
      const defaultRate = work.press_rate ?? PRESS_RATES[work.garment_part] ?? PRESS_RATES['OTHER'];

      entries[key] = {
        work,
        include: true,
        quantity: work.quantity_available,
        press_rate: defaultRate,
        addSampleQty: false
      };
    });

    setIssueEntries(entries);
    setIssueWorkerId('');
    setIssueDoReceiveEntry(false);
    setIssueDesignInfo({
      design_no: group.design_no,
      design_image: group.design_image || null
    });
    setIssueDialogOpen(true);
  };

  const handleIssueDialogClose = () => {
    setIssueDialogOpen(false);
    setIssueEntries({});
    setIssueDesignInfo(null);
    setIssueWorkerId('');
    setIssueDoReceiveEntry(false);
  };

  const handleIssueEntryToggle = (key: string, include: boolean) => {
    setIssueEntries(prev => {
      if (!prev[key]) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          include
        }
      };
    });
  };

  const handleIssueEntryQuantityChange = (key: string, value: number) => {
    setIssueEntries(prev => {
      const entry = prev[key];
      if (!entry) return prev;
      const maxQty = entry.work.quantity_available + (entry.addSampleQty ? 1 : 0);
      const clamped = Math.max(0, Math.min(value, maxQty));
      return {
        ...prev,
        [key]: {
          ...entry,
          quantity: clamped
        }
      };
    });
  };

  const handleIssueEntryRateChange = (key: string, value: number) => {
    setIssueEntries(prev => {
      const entry = prev[key];
      if (!entry) return prev;
      const sanitized = value > 0 ? value : 0;
      return {
        ...prev,
        [key]: {
          ...entry,
          press_rate: sanitized
        }
      };
    });
  };

  const handleIssueEntrySampleQtyToggle = (key: string, checked: boolean) => {
    setIssueEntries(prev => {
      const entry = prev[key];
      if (!entry) return prev;

      // If checking, uncheck all other entries for the same garment part
      if (checked) {
        const updated: Record<string, IssueDialogEntry> = {};
        Object.entries(prev).forEach(([k, e]) => {
          if (e.work.garment_part === entry.work.garment_part) {
            if (k === key) {
              // For the selected entry, check sample and auto-increment quantity
              const maxQty = e.work.quantity_available + 1;
              updated[k] = {
                ...e,
                addSampleQty: true,
                quantity: Math.min(e.quantity + 1, maxQty)
              };
            } else {
              // For other entries in same garment part, uncheck sample and decrement if needed
              updated[k] = {
                ...e,
                addSampleQty: false,
                quantity: e.addSampleQty ? Math.max(0, e.quantity - 1) : e.quantity
              };
            }
          } else {
            updated[k] = e;
          }
        });
        return updated;
      } else {
        // If unchecking, decrement quantity by 1 (but not below 0)
        return {
          ...prev,
          [key]: {
            ...entry,
            addSampleQty: false,
            quantity: Math.max(0, entry.quantity - 1)
          }
        };
      }
    });
  };

  const handleToggleAllIssueEntries = (checked: boolean) => {
    setIssueEntries(prev => {
      const next: Record<string, IssueDialogEntry> = {};
      Object.entries(prev).forEach(([key, entry]) => {
        next[key] = { ...entry, include: checked };
      });
      return next;
    });
  };

  const totalIssueQuantity = useMemo(() => {
    return Object.values(issueEntries).reduce((sum, entry) => {
      if (!entry.include) return sum;
      return sum + entry.quantity + (entry.addSampleQty ? 1 : 0);
    }, 0);
  }, [issueEntries]);

  const selectedIssueEntriesCount = useMemo(() => {
    return Object.values(issueEntries).filter(entry => entry.include && entry.quantity > 0).length;
  }, [issueEntries]);

  const allIssueEntriesSelected = useMemo(() => {
    const entries = Object.values(issueEntries);
    if (entries.length === 0) return false;
    return entries.every(entry => entry.include);
  }, [issueEntries]);

  const issueDialogTotalAvailable = useMemo(() => {
    return Object.values(issueEntries).reduce((sum, entry) => sum + entry.work.quantity_available, 0);
  }, [issueEntries]);

  const issueEntriesArray = useMemo(() => Object.entries(issueEntries), [issueEntries]);

  const selectedManualDesign = useMemo(() => {
    if (!manualJobForm.design_no) return null;
    return designOptions.find(option => option.design_no === manualJobForm.design_no) || null;
  }, [manualJobForm.design_no, designOptions]);

  const handleIssueSubmit = async () => {
    try {
      if (!issueDesignInfo) {
        setError('No design selected for issuing.');
        return;
      }

      if (!issueWorkerId) {
        setError('Please select a worker.');
        return;
      }

      const entriesToIssue = Object.values(issueEntries).filter(entry => entry.include && entry.quantity > 0);

      if (entriesToIssue.length === 0) {
        setError('Select at least one garment part/size to issue.');
        return;
      }

      const invalidEntry = entriesToIssue.find(entry => {
        const maxQty = entry.work.quantity_available + (entry.addSampleQty ? 1 : 0);
        return entry.quantity > maxQty || entry.press_rate <= 0;
      });
      if (invalidEntry) {
        setError('Please ensure quantities and press rates are valid.');
        return;
      }

      setLoading(true);

      for (const entry of entriesToIssue) {
        const payload: IssueWorkDTO = {
          design_no: entry.work.design_no,
          garment_part: entry.work.garment_part,
          size: entry.work.size,
          quantity: entry.quantity,
          worker_id: issueWorkerId,
          press_rate: entry.press_rate,
          add_sample_qty: entry.addSampleQty
        };

        if (entry.work.batch_id) {
          payload.batch_id = entry.work.batch_id;
        }

        const issueResult = await pressApi.issueWork(payload);

        if (issueDoReceiveEntry && issueResult.id) {
          // Calculate final quantity including sample
          const finalQuantity = entry.quantity + (entry.addSampleQty ? 1 : 0);
          await pressApi.receiveWork(issueResult.id, {
            quantity_received: finalQuantity,
            defect_quantity: 0,
            repair_type_id: null,
            repair_notes: '',
            notes: 'Auto-received with issue'
          });
        }
      }

      setSuccess(issueDoReceiveEntry ? 'Press work issued and received successfully!' : 'Press work issued successfully!');
      setIssueDialogOpen(false);
      setIssueEntries({});
      setIssueDesignInfo(null);
      setIssueWorkerId('');
      fetchData();
    } catch (err: any) {
      console.error('Error issuing work:', err);
      setError(err.response?.data?.error || 'Failed to issue work');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveOpen = (work: PressWork) => {
    setSelectedWork(work);
    setReceiveForm({
      quantity_received: work.quantity_issued || 0,
      defect_quantity: 0,
      repair_type_id: null,
      repair_notes: '',
      notes: ''
    });
    setReceiveDialogOpen(true);
  };

  const handleReceiveSubmit = async () => {
    try {
      if (!selectedWork || !selectedWork.assignment_id) {
        setError('Invalid work assignment');
        return;
      }

      const total = receiveForm.quantity_received + receiveForm.defect_quantity;
      if (total !== (selectedWork.quantity_issued || 0)) {
        setError(`Total must equal issued quantity (${selectedWork.quantity_issued})`);
        return;
      }

      if (receiveForm.defect_quantity > 0 && !receiveForm.repair_type_id) {
        setError('Please select repair type for damaged pieces');
        return;
      }

      setLoading(true);
      await pressApi.receiveWork(selectedWork.assignment_id, {
        quantity_received: receiveForm.quantity_received,
        defect_quantity: receiveForm.defect_quantity,
        repair_type_id: receiveForm.repair_type_id,
        repair_notes: receiveForm.repair_notes,
        notes: receiveForm.notes
      });

      setSuccess('Press work received successfully! Batch moved to QC.');
      setReceiveDialogOpen(false);
      fetchData();
    } catch (err: any) {
      console.error('Error receiving work:', err);
      setError(err.response?.data?.error || 'Failed to receive work');
    } finally {
      setLoading(false);
    }
  };

  const handleManualJobSubmit = async () => {
    try {
      if (!manualJobForm.design_no || !manualJobForm.garment_part || manualJobForm.quantity <= 0) {
        setError('Please fill all required fields');
        return;
      }
      if (!manualJobForm.worker_id) {
        setError('Please select a worker');
        return;
      }
      if (!manualJobForm.press_rate || manualJobForm.press_rate <= 0) {
        setError('Please enter a valid press rate');
        return;
      }

      setLoading(true);
      await pressApi.createManualJob(manualJobForm);

      setSuccess('Manual press job created and assigned to worker!');
      setManualJobDialogOpen(false);
      setManualJobForm({
        design_no: '',
        garment_part: '',
        size: '',
        quantity: 0,
        worker_id: '',
        press_rate: 0
      });
      fetchData();
    } catch (err: any) {
      console.error('Error creating manual job:', err);
      setError(err.response?.data?.error || 'Failed to create manual job');
    } finally {
      setLoading(false);
    }
  };

  // Helper to format date for input[type="date"] without timezone issues
  const formatDateForInput = (dateValue: Date | string | undefined | null): string => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    // Use local date parts to avoid timezone shifting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleEditOpen = (work: PressWork) => {
    setEditingWork(work);
    
    setEditForm({
      worker_id: work.worker_id || '',
      quantity_issued: work.quantity_issued || work.quantity_available || 0,
      press_rate: work.press_rate || 0,
      issue_date: formatDateForInput(work.issue_date),
      receive_date: formatDateForInput(work.receive_date),
      quantity_received: work.quantity_received || 0,
      defect_quantity: work.defect_quantity || 0,
      notes: ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      if (!editingWork || !editingWork.assignment_id) {
        setError('Invalid work assignment');
        return;
      }

      setLoading(true);
      let hasUpdates = false;

      // Update assignment details (worker, rate, issue_date)
      const assignmentUpdate: any = {};
      
      if (editForm.worker_id && editForm.worker_id !== editingWork.worker_id) {
        assignmentUpdate.worker_id = editForm.worker_id;
      }
      
      if (editForm.press_rate !== (editingWork.press_rate || 0)) {
        assignmentUpdate.press_rate = editForm.press_rate;
      }
      
      if (editForm.issue_date) {
        const existingDate = formatDateForInput(editingWork.issue_date);
        if (editForm.issue_date !== existingDate) {
          assignmentUpdate.issue_date = editForm.issue_date;
        }
      }

      // For non-received entries, also allow quantity update
      if (editingWork.status !== 'RECEIVED') {
        if (editForm.quantity_issued !== (editingWork.quantity_issued || 0)) {
          assignmentUpdate.quantity_issued = editForm.quantity_issued;
        }
      }

      // Update assignment if there are changes
      if (Object.keys(assignmentUpdate).length > 0) {
        await pressApi.updateAssignment(editingWork.assignment_id, assignmentUpdate);
        hasUpdates = true;
      }

      // For received entries, also update the receive record
      if (editingWork.status === 'RECEIVED' && editingWork.receive_id) {
        const receiveUpdate: any = {};
        
        if (editForm.quantity_received !== (editingWork.quantity_received || 0)) {
          receiveUpdate.quantity_received = editForm.quantity_received;
        }
        
        if (editForm.defect_quantity !== (editingWork.defect_quantity || 0)) {
          receiveUpdate.defect_quantity = editForm.defect_quantity;
        }
        
        if (editForm.receive_date) {
          const existingReceiveDate = formatDateForInput(editingWork.receive_date);
          if (editForm.receive_date !== existingReceiveDate) {
            receiveUpdate.receive_date = editForm.receive_date;
          }
        }

        if (Object.keys(receiveUpdate).length > 0) {
          await pressApi.updateReceive(editingWork.receive_id, receiveUpdate);
          hasUpdates = true;
        }
      }

      // Refresh data first so the grid updates
      await fetchData();
      
      if (hasUpdates) {
        setSuccess('Entry updated successfully!');
      } else {
        setSuccess('No changes to save');
      }
      
      setEditDialogOpen(false);
      setEditingWork(null);
    } catch (err: any) {
      console.error('Error updating entry:', err);
      setError(err.response?.data?.error || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDesignPreview = async (designNo: string, previewImage?: string | null) => {
    setSelectedDesignNo(designNo);
    setSelectedPreviewImage(previewImage || null);
    setLoadingCuttingData(true);
    setPreviewDialogOpen(true);
    setSelectedPreviewDesign(null);
    setSelectedPreviewTopDesign(null);
    setSelectedPreviewBottomDesign(null);

    try {
      // Fetch design by design_no
      const designsResponse = await getDesigns();
      const designs = Array.isArray(designsResponse?.data)
        ? designsResponse.data
        : Array.isArray(designsResponse)
          ? designsResponse
          : [];
      const design = designs.find((d: any) => d.design_no === designNo);

      if (design) {
        // Fetch full design details
        try {
          const fullDesign = await designApi.getById(design.id);
          // Handle both response.data and direct response
          if (fullDesign?.data) {
            setSelectedPreviewDesign(fullDesign.data);
          } else if (fullDesign) {
            setSelectedPreviewDesign(fullDesign);
          } else {
            setSelectedPreviewDesign(design);
          }
        } catch (e: any) {
          console.error('Error fetching full design details:', e);
          // Use the basic design object if full fetch fails
          setSelectedPreviewDesign(design);
        }
      } else {
        console.warn(`Design ${designNo} not found in designs list`);
      }

      // Fetch design cutting data
      const cuttingResponse = await api.get('/design-cutting');
      if (cuttingResponse.data && Array.isArray(cuttingResponse.data)) {
        const allCuttingData = cuttingResponse.data;
        const topDesign = allCuttingData.find((d: any) =>
          d.design_no === designNo && d.garment_part === 'TOP'
        );
        const bottomDesign = allCuttingData.find((d: any) =>
          d.design_no === designNo && d.garment_part === 'BOTTOM'
        );
        setSelectedPreviewTopDesign(topDesign);
        setSelectedPreviewBottomDesign(bottomDesign);
      }
    } catch (error) {
      console.error('Error fetching design preview data:', error);
    } finally {
      setLoadingCuttingData(false);
    }
  };

  const handleCloseDesignPreview = () => {
    setPreviewDialogOpen(false);
    setSelectedPreviewDesign(null);
    setSelectedPreviewTopDesign(null);
    setSelectedPreviewBottomDesign(null);
    setSelectedDesignNo(null);
    setSelectedPreviewImage(null);
    setLoadingCuttingData(false);
  };

  const renderWorkTable = (works: PressWork[], showActions: 'receive' | 'none') => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Design</TableCell>
            <TableCell>Garment Part</TableCell>
            <TableCell>Size</TableCell>
            <TableCell align="center">Quantity</TableCell>
            <TableCell>Worker</TableCell>
            <TableCell align="right">Rate</TableCell>
            {showActions === 'receive' && <TableCell>Issue Date</TableCell>}
            {showActions === 'none' && <TableCell>Receive Date</TableCell>}
            {showActions === 'none' && <TableCell align="center">Received</TableCell>}
            {showActions === 'none' && <TableCell align="center">Defects</TableCell>}
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={12} align="center">
                Loading...
              </TableCell>
            </TableRow>
          ) : works.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} align="center">
                <Box sx={{ py: 4 }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No items in this category
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            works.map((work, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Avatar
                    src={work.design_image || ''}
                    variant="rounded"
                    sx={{ width: 40, height: 40, cursor: 'pointer' }}
                    onClick={() => handleOpenDesignPreview(work.design_no, work.design_image)}
                  >
                    {work.design_no.substring(0, 2)}
                  </Avatar>
                </TableCell>
                <TableCell>{work.design_no}</TableCell>
                <TableCell>{work.garment_part}</TableCell>
                <TableCell>
                  <Chip label={work.size} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="bold">
                    {work.quantity_issued ?? work.quantity_available}
                  </Typography>
                </TableCell>
                <TableCell>{work.worker_name || '-'}</TableCell>
                <TableCell align="right">₹{work.press_rate?.toFixed(2)}</TableCell>
                {showActions === 'receive' && (
                  <TableCell>
                    {work.issue_date ? new Date(work.issue_date).toLocaleDateString() : '-'}
                  </TableCell>
                )}
                {showActions === 'none' && (
                  <>
                    <TableCell>
                      {work.receive_date ? new Date(work.receive_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={work.quantity_received || 0} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={work.defect_quantity || 0}
                        color={work.defect_quantity ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </>
                )}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {showActions === 'receive' && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleReceiveOpen(work)}
                      >
                        Receive
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditOpen(work)}
                      title="Edit Entry"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPendingDesignCards = () => {
    if (loading) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Loading pending designs...
          </Typography>
        </Paper>
      );
    }

    if (pendingDesignGroups.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No pending designs to issue
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {pendingDesignGroups.map(group => (
          <Grid item xs={6} sm={4} md={3} key={group.design_no}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 12px 24px rgba(15,23,42,0.1)'
              }}
            >
              <Box
                component="img"
                src={group.design_image || '/images/design-placeholder.jpg'}
                alt={group.design_no}
                onClick={() => handleOpenDesignPreview(group.design_no, group.design_image)}
                sx={{
                  width: '100%',
                  height: isMobile ? 260 : 360,
                  objectFit: 'cover',
                  cursor: 'pointer'
                }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="overline" sx={{ letterSpacing: 2, color: 'text.secondary' }}>
                  PRESS PENDING
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {group.design_no}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.total_quantity} pcs awaiting issue
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  {group.garments.map(part => (
                    <Box
                      key={`${group.design_no}-${part.garment_part}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {part.garment_part}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {part.total_quantity} pcs
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AssignmentIcon />}
                  onClick={() => handleIssueOpen(group)}
                >
                  Issue Work
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleReceivedDetailsOpen = (group: ReceivedDesignGroup) => {
    setSelectedReceivedGroup(group);
    setReceivedDetailsDialogOpen(true);
  };

  const renderReceivedDesignCards = () => {
    if (loading) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Loading received work...
          </Typography>
        </Paper>
      );
    }

    if (receivedDesignGroups.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No work received in this period
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {receivedDesignGroups.map(group => (
          <Grid item xs={6} sm={4} md={3} key={group.design_no}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 12px 24px rgba(15,23,42,0.1)',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => handleReceivedDetailsOpen(group)}
            >
              <Box
                component="img"
                src={group.design_image || '/images/design-placeholder.jpg'}
                alt={group.design_no}
                sx={{
                  width: '100%',
                  height: isMobile ? 260 : 360,
                  objectFit: 'cover'
                }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="overline" sx={{ letterSpacing: 2, color: 'success.main', fontWeight: 'bold' }}>
                  RECEIVED
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {group.design_no}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Received: <strong>{group.total_received} pcs</strong>
                </Typography>
                {group.total_defects > 0 && (
                  <Typography variant="body2" color="error.main">
                    Defects: {group.total_defects} pcs
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Press Station
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage press work assignments and receives
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setManualJobDialogOpen(true)}
          >
            Add Manual Job
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Pending Assignment
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {pendingWork.reduce((sum, w) => sum + w.quantity_available, 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {pendingWork.length} batch(es)
                </Typography>
                {(() => {
                  const partMap: Record<string, number> = {};
                  pendingWork.forEach(w => {
                    partMap[w.garment_part] = (partMap[w.garment_part] || 0) + w.quantity_available;
                  });
                  const parts = Object.entries(partMap).sort((a, b) => b[1] - a[1]);
                  return parts.length > 1 ? (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {parts.map(([part, qty]) => (
                        <Chip key={part} label={`${part}: ${qty}`} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : null;
                })()}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Assigned to Workers
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {assignedWork.reduce((sum, w) => sum + (w.quantity_issued || 0), 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {assignedWork.length} assignment(s)
                </Typography>
                {(() => {
                  const partMap: Record<string, number> = {};
                  assignedWork.forEach(w => {
                    partMap[w.garment_part] = (partMap[w.garment_part] || 0) + (w.quantity_issued || 0);
                  });
                  const parts = Object.entries(partMap).sort((a, b) => b[1] - a[1]);
                  return parts.length > 1 ? (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {parts.map(([part, qty]) => (
                        <Chip key={part} label={`${part}: ${qty}`} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : null;
                })()}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Received Today
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {receivedWork.reduce((sum, w) => sum + (w.quantity_received || 0), 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {receivedWork.length} receive(s)
                </Typography>
                {(() => {
                  const partMap: Record<string, number> = {};
                  receivedWork.forEach(w => {
                    partMap[w.garment_part] = (partMap[w.garment_part] || 0) + (w.quantity_received || 0);
                  });
                  const parts = Object.entries(partMap).sort((a, b) => b[1] - a[1]);
                  return parts.length > 1 ? (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {parts.map(([part, qty]) => (
                        <Chip key={part} label={`${part}: ${qty}`} size="small" variant="outlined" />
                      ))}
                    </Box>
                  ) : null;
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Filter */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search by Design"
            variant="outlined"
            size="small"
            value={designSearchFilter}
            onChange={(e) => setDesignSearchFilter(e.target.value)}
            placeholder="Enter design number..."
            sx={{ minWidth: 250 }}
          />
          {designSearchFilter && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDesignSearchFilter('')}
            >
              Clear Filter
            </Button>
          )}
          {designFilter && (
            <Chip
              label={`Filtered by: ${designFilter}`}
              color="primary"
              onDelete={() => {
                setDesignSearchFilter('');
                navigate('/production/press');
              }}
            />
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab
              label={`Pending (${filteredPendingWork.length}${designSearchFilter ? ` of ${pendingWork.length}` : ''})`}
              icon={<AssignmentIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Assigned (${filteredAssignedWork.length}${designSearchFilter ? ` of ${assignedWork.length}` : ''})`}
              icon={<BuildIcon />}
              iconPosition="start"
            />
            <Tab
              label={`Received (${filteredReceivedWork.length}${designSearchFilter ? ` of ${receivedWork.length}` : ''})`}
              icon={<CheckCircleIcon />}
              iconPosition="start"
            />
            <Tab
              label="Worker Report"
              icon={<AssessmentIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Alert severity="info" sx={{ mb: 2 }}>
            These pieces have been sent from Dhaga Cutting station and are waiting to be assigned to press workers.
          </Alert>
          {renderPendingDesignCards()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            These pieces have been issued to workers and are currently being pressed.
          </Alert>
          {renderWorkTable(filteredAssignedWork, 'receive')}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">
              These pieces have been received and automatically moved to Final QC stage.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                select
                label="Date Filter"
                size="small"
                value={receivedDateFilter}
                onChange={(e) => {
                  const value = e.target.value as 'today' | 'week' | 'month' | 'quarter' | 'custom';
                  setReceivedDateFilter(value);
                  // Set default dates for custom filter
                  if (value === 'custom' && !customDateFrom) {
                    const today = new Date();
                    setCustomDateFrom(today.toISOString().split('T')[0]);
                    setCustomDateTo(today.toISOString().split('T')[0]);
                  }
                }}
                sx={{ minWidth: 150, bgcolor: 'background.paper' }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </TextField>
              {receivedDateFilter === 'custom' && (
                <>
                  <TextField
                    type="date"
                    label="From Date"
                    size="small"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  />
                  <TextField
                    type="date"
                    label="To Date"
                    size="small"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  />
                </>
              )}
            </Box>
          </Box>
          {renderReceivedDesignCards()}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Worker-wise summary of press pieces issued and received.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                select
                label="Date Filter"
                size="small"
                value={workerReportDateFilter}
                onChange={(e) => {
                  const value = e.target.value as 'today' | 'week' | 'month' | 'quarter' | 'custom';
                  setWorkerReportDateFilter(value);
                  if (value === 'custom' && !workerReportFromDate) {
                    const today = new Date();
                    setWorkerReportFromDate(today.toISOString().split('T')[0]);
                    setWorkerReportToDate(today.toISOString().split('T')[0]);
                  }
                }}
                sx={{ minWidth: 150, bgcolor: 'background.paper' }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </TextField>
              {workerReportDateFilter === 'custom' && (
                <>
                  <TextField
                    type="date"
                    label="From Date"
                    size="small"
                    value={workerReportFromDate}
                    onChange={(e) => setWorkerReportFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  />
                  <TextField
                    type="date"
                    label="To Date"
                    size="small"
                    value={workerReportToDate}
                    onChange={(e) => setWorkerReportToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  />
                </>
              )}
            </Box>
          </Box>

          {workerReportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : workerReportData.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No press work data for the selected period
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Worker</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Issues</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Pcs Issued</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Issue Amt (₹)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Receives</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Pcs Received</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Receive Amt (₹)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Defects</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pending</TableCell>
                    {workerReportGarmentParts.map(part => (
                      <TableCell key={part} align="center" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', bgcolor: 'grey.50' }}>
                        {part.replace(/_/g, ' ')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workerReportData.map((row) => (
                    <TableRow key={row.worker_id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ whiteSpace: 'nowrap', cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => fetchWorkerDetail(row.worker_id, row.worker_name)}
                        >
                          {row.worker_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{row.assignments_count}</TableCell>
                      <TableCell align="center">
                        <Chip label={row.total_issued} color="info" size="small" />
                      </TableCell>
                      <TableCell align="right">₹{row.total_issued_amount.toFixed(2)}</TableCell>
                      <TableCell align="center">{row.receives_count}</TableCell>
                      <TableCell align="center">
                        <Chip label={row.total_received} color="success" size="small" />
                      </TableCell>
                      <TableCell align="right">₹{row.total_received_amount.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        {row.total_defects > 0 ? (
                          <Chip label={row.total_defects} color="error" size="small" />
                        ) : (
                          <Chip label="0" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.total_issued - row.total_received - row.total_defects}
                          color={row.total_issued - row.total_received - row.total_defects > 0 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      {workerReportGarmentParts.map(part => {
                        const bp = row.garment_part_breakdown?.[part];
                        const issued = bp?.issued || 0;
                        const received = bp?.received || 0;
                        return (
                          <TableCell key={part} align="center" sx={{ bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                            {issued > 0 || received > 0 ? (
                              <Typography variant="caption">
                                <span style={{ color: '#1976d2' }}>{issued}i</span>
                                {' / '}
                                <span style={{ color: '#2e7d32' }}>{received}r</span>
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        Total ({workerReportData.length} workers)
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {workerReportData.reduce((s, r) => s + r.assignments_count, 0)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={workerReportData.reduce((s, r) => s + r.total_issued, 0)}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ₹{workerReportData.reduce((s, r) => s + r.total_issued_amount, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      {workerReportData.reduce((s, r) => s + r.receives_count, 0)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={workerReportData.reduce((s, r) => s + r.total_received, 0)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ₹{workerReportData.reduce((s, r) => s + r.total_received_amount, 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={workerReportData.reduce((s, r) => s + r.total_defects, 0)}
                        color={workerReportData.reduce((s, r) => s + r.total_defects, 0) > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={workerReportData.reduce((s, r) => s + r.total_issued - r.total_received - r.total_defects, 0)}
                        color={workerReportData.reduce((s, r) => s + r.total_issued - r.total_received - r.total_defects, 0) > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    {workerReportGarmentParts.map(part => {
                      const totalIssued = workerReportData.reduce((s, r) => s + (r.garment_part_breakdown?.[part]?.issued || 0), 0);
                      const totalReceived = workerReportData.reduce((s, r) => s + (r.garment_part_breakdown?.[part]?.received || 0), 0);
                      return (
                        <TableCell key={part} align="center" sx={{ bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                          {totalIssued > 0 || totalReceived > 0 ? (
                            <Typography variant="caption" fontWeight="bold">
                              <span style={{ color: '#1976d2' }}>{totalIssued}i</span>
                              {' / '}
                              <span style={{ color: '#2e7d32' }}>{totalReceived}r</span>
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Worker Detail Dialog */}
      <Dialog
        open={workerDetailOpen}
        onClose={() => setWorkerDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{workerDetailName} — Press Details</Typography>
          <Button onClick={() => setWorkerDetailOpen(false)} size="small">Close</Button>
        </DialogTitle>
        <DialogContent dividers>
          {workerDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : workerDetailData.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No assignment data found for this worker in the selected period.
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`${workerDetailData.length} assignments`} variant="outlined" />
                <Chip label={`Issued: ${workerDetailData.reduce((s: number, r: any) => s + r.quantity_issued, 0)} pcs`} color="info" size="small" />
                <Chip label={`Received: ${workerDetailData.reduce((s: number, r: any) => s + r.quantity_received, 0)} pcs`} color="success" size="small" />
                <Chip label={`Amount: ₹${workerDetailData.reduce((s: number, r: any) => s + r.receive_amount, 0).toFixed(2)}`} color="default" size="small" />
              </Box>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Design No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Garment Part</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Issued</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Received</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Defects</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pending</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate (₹)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Amount (₹)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Issue Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Receive Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workerDetailData.map((row: any) => (
                      <TableRow key={row.assignment_id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.design_no}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.garment_part?.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{row.size}</TableCell>
                        <TableCell align="center">{row.quantity_issued}</TableCell>
                        <TableCell align="center">{row.quantity_received}</TableCell>
                        <TableCell align="center">
                          {row.defect_quantity > 0 ? (
                            <Chip label={row.defect_quantity} color="error" size="small" />
                          ) : (
                            row.defect_quantity
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {row.pending > 0 ? (
                            <Chip label={row.pending} color="warning" size="small" />
                          ) : (
                            row.pending
                          )}
                        </TableCell>
                        <TableCell align="right">₹{row.rate?.toFixed(2)}</TableCell>
                        <TableCell align="right">₹{row.receive_amount?.toFixed(2)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          {row.issue_date ? new Date(row.issue_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          {row.receive_date ? new Date(row.receive_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={row.status === 'COMPLETED' ? 'success' : row.status === 'PENDING' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" fontWeight="bold">Total</Typography>
                      </TableCell>
                      <TableCell align="center">{workerDetailData.reduce((s: number, r: any) => s + r.quantity_issued, 0)}</TableCell>
                      <TableCell align="center">{workerDetailData.reduce((s: number, r: any) => s + r.quantity_received, 0)}</TableCell>
                      <TableCell align="center">{workerDetailData.reduce((s: number, r: any) => s + r.defect_quantity, 0)}</TableCell>
                      <TableCell align="center">{workerDetailData.reduce((s: number, r: any) => s + r.pending, 0)}</TableCell>
                      <TableCell />
                      <TableCell align="right">₹{workerDetailData.reduce((s: number, r: any) => s + r.receive_amount, 0).toFixed(2)}</TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog
        open={issueDialogOpen}
        onClose={handleIssueDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Issue Press Work</DialogTitle>
        <DialogContent>
          {issueDesignInfo && (
            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Design:</strong> {issueDesignInfo.design_no}<br />
                <strong>Total Pending Quantity:</strong> {issueDialogTotalAvailable} pieces
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={workers}
                getOptionLabel={(option) => option.name}
                value={workers.find(w => w.worker_id === issueWorkerId) || null}
                onChange={(e, newValue) => setIssueWorkerId(newValue?.worker_id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Select Worker" required />
                )}
              />
            </Grid>
          </Grid>

          {isMobile ? (
            <Stack spacing={2} sx={{ mt: 3 }}>
              {issueEntriesArray.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No pending pieces available for this design.
                  </Typography>
                </Paper>
              ) : (
                issueEntriesArray.map(([key, entry]) => (
                  <Paper key={key} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            checked={entry.include}
                            onChange={(e) => handleIssueEntryToggle(key, e.target.checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight="bold">
                              {entry.work.garment_part} · {entry.work.size}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Available {entry.work.quantity_available} pcs
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip label={`${entry.work.quantity_available} pcs`} size="small" />
                    </Box>
                    <Grid container spacing={1.5} sx={{ mt: 2 }}>
                      <Grid item xs={6}>
                        <TextField
                          label="Quantity"
                          type="number"
                          size="small"
                          value={entry.quantity}
                          onChange={(e) => handleIssueEntryQuantityChange(key, parseInt(e.target.value, 10) || 0)}
                          InputProps={{ inputProps: { min: 0, max: entry.work.quantity_available + (entry.addSampleQty ? 1 : 0) } }}
                          fullWidth
                          helperText={entry.addSampleQty ? `Max: ${entry.work.quantity_available + 1} (includes sample)` : undefined}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Rate (₹)"
                          type="number"
                          size="small"
                          value={entry.press_rate}
                          onChange={(e) => handleIssueEntryRateChange(key, parseFloat(e.target.value) || 0)}
                          InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={entry.addSampleQty}
                              onChange={(e) => handleIssueEntrySampleQtyToggle(key, e.target.checked)}
                              disabled={!entry.include}
                              size="small"
                            />
                          }
                          label="Add Sample Qty (+1 piece)"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Stack>
          ) : (
            <Table size="small" sx={{ mt: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={!allIssueEntriesSelected && issueEntriesArray.some(([, entry]) => entry.include)}
                      checked={allIssueEntriesSelected}
                      onChange={(e) => handleToggleAllIssueEntries(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Garment Part</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="center">Available</TableCell>
                  <TableCell align="center">Quantity to Issue</TableCell>
                  <TableCell align="center">Press Rate (₹)</TableCell>
                  <TableCell align="center">Add Sample Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issueEntriesArray.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No pending pieces available for this design.
                    </TableCell>
                  </TableRow>
                ) : (
                  issueEntriesArray.map(([key, entry]) => {
                    const maxQty = entry.work.quantity_available + (entry.addSampleQty ? 1 : 0);
                    return (
                      <TableRow key={key}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={entry.include}
                            onChange={(e) => handleIssueEntryToggle(key, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{entry.work.garment_part}</TableCell>
                        <TableCell>{entry.work.size}</TableCell>
                        <TableCell align="center">
                          <Chip label={entry.work.quantity_available} color="default" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={entry.quantity}
                            onChange={(e) => handleIssueEntryQuantityChange(key, parseInt(e.target.value, 10) || 0)}
                            InputProps={{ inputProps: { min: 0, max: maxQty } }}
                            sx={{ width: 120 }}
                            helperText={entry.addSampleQty ? `Max: ${maxQty} (includes sample)` : undefined}
                            FormHelperTextProps={{ sx: { margin: 0, fontSize: '0.7rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={entry.press_rate}
                            onChange={(e) => handleIssueEntryRateChange(key, parseFloat(e.target.value) || 0)}
                            InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={entry.addSampleQty}
                            onChange={(e) => handleIssueEntrySampleQtyToggle(key, e.target.checked)}
                            disabled={!entry.include}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}
          >
            <Typography variant="body2">
              Selected rows: {selectedIssueEntriesCount} | Total quantity: {totalIssueQuantity}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ensure the quantity does not exceed what's available for each size.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={issueDoReceiveEntry}
                  onChange={(e) => setIssueDoReceiveEntry(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Do Receive Entry (Issue + Receive together)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check this if work is completed. System will issue and immediately receive the work.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIssueDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleIssueSubmit}
            disabled={loading || selectedIssueEntriesCount === 0 || totalIssueQuantity === 0}
          >
            {issueDoReceiveEntry ? 'Issue & Receive Selected' : 'Issue Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Press Work</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Worker:</strong> {selectedWork?.worker_name}<br />
                <strong>Issued:</strong> {selectedWork?.quantity_issued} pieces<br />
                <strong>Rate:</strong> ₹{selectedWork?.press_rate}/pc
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity Received (Good)"
                  type="number"
                  value={receiveForm.quantity_received}
                  onChange={(e) => setReceiveForm({ ...receiveForm, quantity_received: parseInt(e.target.value) || 0 })}
                  InputProps={{ inputProps: { min: 0, max: selectedWork?.quantity_issued || 0 } }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Defect Quantity (Damaged)"
                  type="number"
                  value={receiveForm.defect_quantity}
                  onChange={(e) => setReceiveForm({ ...receiveForm, defect_quantity: parseInt(e.target.value) || 0 })}
                  InputProps={{ inputProps: { min: 0, max: selectedWork?.quantity_issued || 0 } }}
                />
              </Grid>

              {receiveForm.defect_quantity > 0 && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Damage Type"
                      value={receiveForm.repair_type_id || ''}
                      onChange={(e) => setReceiveForm({ ...receiveForm, repair_type_id: parseInt(e.target.value) })}
                      required
                    >
                      <MenuItem value={1}>Press Mark (Burn)</MenuItem>
                      <MenuItem value={2}>Fabric Damage</MenuItem>
                      <MenuItem value={3}>Other Press Issue</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Repair Notes"
                      multiline
                      rows={2}
                      value={receiveForm.repair_notes}
                      onChange={(e) => setReceiveForm({ ...receiveForm, repair_notes: e.target.value })}
                      placeholder="Describe the damage..."
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Receive Notes"
                  multiline
                  rows={2}
                  value={receiveForm.notes}
                  onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })}
                  placeholder="Any observations..."
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Total:</strong> {receiveForm.quantity_received + receiveForm.defect_quantity} / {selectedWork?.quantity_issued}
                {receiveForm.quantity_received + receiveForm.defect_quantity === selectedWork?.quantity_issued ? (
                  <Chip label="✓ Valid" color="success" size="small" sx={{ ml: 1 }} />
                ) : (
                  <Chip label="✗ Must match issued" color="error" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Payment:</strong> ₹{((selectedWork?.press_rate || 0) * receiveForm.quantity_received).toFixed(2)}
              </Typography>
            </Box>

            {receiveForm.defect_quantity > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {receiveForm.defect_quantity} damaged piece(s) will be added to Repair Queue
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleReceiveSubmit} disabled={loading}>
            Receive & Move to QC
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Job Dialog */}
      <Dialog open={manualJobDialogOpen} onClose={() => setManualJobDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Manual Press Job</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Create a manual press job for one-off items like dupatta, belt, etc.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={designOptions}
                  loading={designsLoading}
                  value={selectedManualDesign}
                  onChange={(e, newValue) => {
                    setManualJobForm(prev => ({
                      ...prev,
                      design_no: newValue?.design_no || '',
                      garment_part: newValue?.garment_part || prev.garment_part
                    }));
                  }}
                  onInputChange={(e, value, reason) => {
                    if (reason === 'input') {
                      handleDesignSearch(value);
                    }
                  }}
                  getOptionLabel={(option) => option.design_no}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={option.image_url || ''}
                        variant="rounded"
                        sx={{ width: 40, height: 40 }}
                      >
                        {option.design_no.substring(0, 2)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.design_no}
                        </Typography>
                        {option.garment_part && (
                          <Typography variant="caption" color="text.secondary">
                            {option.garment_part}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Design Number"
                      placeholder="Search designs..."
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {designsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              {selectedManualDesign?.image_url && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Selected design preview
                    </Typography>
                    <Box
                      component="img"
                      src={selectedManualDesign.image_url}
                      alt={selectedManualDesign.design_no}
                      sx={{
                        width: '100%',
                        maxHeight: 240,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Garment Part"
                  value={manualJobForm.garment_part}
                  onChange={(e) => {
                    const part = e.target.value;
                    setManualJobForm(prev => ({
                      ...prev,
                      garment_part: part,
                      press_rate: prev.press_rate || PRESS_RATES[part] || PRESS_RATES['OTHER'] || 0
                    }));
                  }}
                  required
                >
                  <MenuItem value="TOP">Top</MenuItem>
                  <MenuItem value="BOTTOM">Bottom</MenuItem>
                  <MenuItem value="DUPATTA">Dupatta</MenuItem>
                  <MenuItem value="BELT">Belt</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Size"
                  value={manualJobForm.size}
                  onChange={(e) => setManualJobForm({ ...manualJobForm, size: e.target.value })}
                  placeholder="e.g., ONE_SIZE"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={manualJobForm.quantity}
                  onChange={(e) => setManualJobForm({ ...manualJobForm, quantity: parseInt(e.target.value) || 0 })}
                  InputProps={{ inputProps: { min: 1 } }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={workers}
                  getOptionLabel={(option) => option.name}
                  value={workers.find(w => w.worker_id === manualJobForm.worker_id) || null}
                  onChange={(e, newValue) => setManualJobForm(prev => ({ ...prev, worker_id: newValue?.worker_id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Worker" required />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Press Rate (₹)"
                  type="number"
                  value={manualJobForm.press_rate}
                  onChange={(e) => setManualJobForm({ ...manualJobForm, press_rate: parseFloat(e.target.value) || 0 })}
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualJobDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleManualJobSubmit} disabled={loading}>
            Create Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Received Details Dialog */}
      <Dialog
        open={receivedDetailsDialogOpen}
        onClose={() => setReceivedDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              Received Details - {selectedReceivedGroup?.design_no}
              <Typography variant="caption" display="block" color="text.secondary">
                Total: {selectedReceivedGroup?.total_received} Good, {selectedReceivedGroup?.total_defects} Defects
              </Typography>
            </Box>
            <IconButton onClick={() => setReceivedDetailsDialogOpen(false)}>
              <AddIcon sx={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReceivedGroup && renderWorkTable(selectedReceivedGroup.items, 'none')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceivedDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Press Entry
          <Typography variant="caption" display="block" color="text.secondary">
            {editingWork?.design_no} - {editingWork?.garment_part} - {editingWork?.size}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Status:</strong> {editingWork?.status}<br />
                <strong>Original Qty:</strong> {editingWork?.quantity_issued || editingWork?.quantity_available} pieces
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={workers}
                  getOptionLabel={(option) => option.name}
                  value={workers.find(w => w.worker_id === editForm.worker_id) || null}
                  onChange={(e, newValue) => setEditForm(prev => ({ ...prev, worker_id: newValue?.worker_id || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Worker" />
                  )}
                />
              </Grid>

              {editingWork?.status !== 'RECEIVED' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Quantity Issued"
                    type="number"
                    value={editForm.quantity_issued}
                    onChange={(e) => setEditForm(prev => ({ ...prev, quantity_issued: parseInt(e.target.value) || 0 }))}
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Press Rate (₹)"
                  type="number"
                  value={editForm.press_rate}
                  onChange={(e) => setEditForm(prev => ({ ...prev, press_rate: parseFloat(e.target.value) || 0 }))}
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Issue Date"
                  type="date"
                  value={editForm.issue_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, issue_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {editingWork?.status === 'RECEIVED' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Receive Date"
                      type="date"
                      value={editForm.receive_date}
                      onChange={(e) => setEditForm(prev => ({ ...prev, receive_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Good Quantity Received"
                      type="number"
                      value={editForm.quantity_received}
                      onChange={(e) => setEditForm(prev => ({ ...prev, quantity_received: parseInt(e.target.value) || 0 }))}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Defect Quantity"
                      type="number"
                      value={editForm.defect_quantity}
                      onChange={(e) => setEditForm(prev => ({ ...prev, defect_quantity: parseInt(e.target.value) || 0 }))}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {editingWork?.status === 'RECEIVED' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  <strong>Note:</strong> Total (Good + Defects) = {editForm.quantity_received + editForm.defect_quantity} / Issued: {editingWork.quantity_issued}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Design Preview Dialog */}
      <DesignPreviewDialog
        open={previewDialogOpen}
        design={selectedPreviewDesign}
        designNo={selectedDesignNo || selectedPreviewDesign?.design_no}
        previewImage={selectedPreviewImage || selectedPreviewDesign?.image_url || null}
        topDesign={selectedPreviewTopDesign}
        bottomDesign={selectedPreviewBottomDesign}
        loadingCuttingData={loadingCuttingData}
        onClose={handleCloseDesignPreview}
      />
    </Box>
  );
}

